// PWA registration + install-prompt plumbing. The browser fires
// `beforeinstallprompt` once when it considers the app installable; we
// stash the event so a UI button can call .prompt() on demand. iOS
// Safari never fires this event — `isIosStandaloneCandidate` lets the
// UI show a manual "Add to Home Screen" hint there instead.

import { useEffect, useState } from "react";
import { showUpdatingOverlay } from "./updating-overlay";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    emit();
  });
}

export function registerServiceWorker(): void {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Defer registration until after first paint so it doesn't compete with
  // the SPA boot.
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", {
        scope: "/",
        // Always fetch sw.js from network — never from HTTP cache.
        // Without this, a stale sw.js in disk cache can block
        // installed PWAs from ever picking up a new SW.
        updateViaCache: "none",
      })
      .then((reg) => {
        // Root-cause guard for the "Update available pill is stuck"
        // bug: a SW can finish installing and sit in `waiting` forever
        // if nothing tells it to take over. The updatefound→statechange
        // path below only catches workers that install AFTER this
        // listener attaches — a worker that was ALREADY waiting when
        // the app launched (installed during a previous session) never
        // gets the skip-waiting message and stalls. Poke it now so it
        // activates → controllerchange fires → the page reloads onto
        // fresh code.
        if (reg.waiting) reg.waiting.postMessage("skip-waiting");
        // Poll for SW updates every 60s so installed PWAs pick up
        // server-side SW changes within a minute of the user opening
        // the app.
        setInterval(() => {
          reg.update().catch(() => {});
        }, 60 * 1000);
        // When the new SW finishes installing and is ready to take
        // over, ask it to skipWaiting + reload the page so the user
        // is on the new code without manual intervention.
        reg.addEventListener("updatefound", () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (
              installing.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              installing.postMessage("skip-waiting");
            }
          });
        });
      })
      .catch((err) => console.warn("SW register failed", err));

    // Reload once the new SW takes control of this page. This is the
    // moment the user actually starts using fresh code.
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading) return;
      reloading = true;
      // Same reason as version-check: a silent reload reads as a hang.
      showUpdatingOverlay();
      window.location.reload();
    });
  });
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari quirk.
  return (window.navigator as { standalone?: boolean }).standalone === true;
}

function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !(window as { MSStream?: unknown }).MSStream;
}

const DISMISS_KEY = "cornholio.pwa.installDismissedAt";
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function recentlyDismissed(): boolean {
  if (typeof window === "undefined") return false;
  const raw = window.localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const at = Number(raw);
  if (!Number.isFinite(at)) return false;
  return Date.now() - at < DISMISS_TTL_MS;
}

export function dismissInstallPrompt(): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  emit();
}

export type InstallState = {
  canPrompt: boolean;
  iosCandidate: boolean;
  installed: boolean;
  dismissed: boolean;
  prompt: () => Promise<"accepted" | "dismissed" | "unavailable">;
};

export function useInstallPrompt(): InstallState {
  const [, setTick] = useState(0);
  useEffect(() => {
    const cb = () => setTick((t) => t + 1);
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  }, []);

  const installed = isStandalone();
  const dismissed = recentlyDismissed();
  const canPrompt = !!deferred && !installed && !dismissed;
  const iosCandidate = !installed && !dismissed && isIos() && !deferred;

  return {
    canPrompt,
    iosCandidate,
    installed,
    dismissed,
    prompt: async () => {
      if (!deferred) return "unavailable";
      await deferred.prompt();
      const choice = await deferred.userChoice;
      deferred = null;
      emit();
      return choice.outcome;
    },
  };
}
